import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios, { API } from '../../services/api';
import { motion } from 'framer-motion';
import { ArrowRight, UploadSimple } from '@phosphor-icons/react';

/**
 * ChatStarterPrompts — Phase 5B + upload-surface sprint.
 *
 * Hybrid: the backend's FIXED list drives the primary prompts (4 document-centric
 * + 2 legacy retention prompts), plus up to 2 AI-contextual prompts.
 *
 * Prompts may carry an `action` field (e.g. '/documents') — those become deep-links
 * instead of seeding the composer, because "Upload a report for review" is an ACTION,
 * not a question.
 *
 * Flat, pill-like, editorial. No icons except the upload affordance.
 */
const FALLBACK_FIXED = [
  { id: 'fx-explain-blood',   text: 'Explain my blood test' },
  { id: 'fx-upload-review',   text: 'Upload a report for review',        action: '/documents' },
  { id: 'fx-what-changed',    text: 'What changed in my latest results?' },
  { id: 'fx-compare-reports', text: 'Compare this report to my last one' },
  { id: 'fx-matters-now',     text: 'What matters most in my health right now?' },
  { id: 'fx-prep-next',       text: 'Prepare me for my next appointment' },
];

export const ChatStarterPrompts = ({ onTap, onModeChange }) => {
  const navigate = useNavigate();
  const [fixed, setFixed] = useState(FALLBACK_FIXED);
  const [dynamic, setDynamic] = useState([]);
  const [dynamicLoaded, setDynamicLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/chat/starter-prompts`);
        if (!cancelled) {
          // Prefer the server's fixed list (has latest copy + action fields)
          if (Array.isArray(data?.fixed) && data.fixed.length) setFixed(data.fixed);
          setDynamic(Array.isArray(data?.dynamic) ? data.dynamic : []);
          // Stage-13 — surface "personalised" vs "examples" mode upward so the
          // empty-state eyebrow can switch from "Start here" to "Try an example".
          if (onModeChange && typeof data?.mode === 'string') onModeChange(data.mode);
        }
      } catch {
        /* silent — fallback fixed prompts stay visible */
      } finally {
        if (!cancelled) setDynamicLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [onModeChange]);

  const all = useMemo(() => {
    // Sprint D — show 4 best contextual prompts (dynamic-first, then fill from fixed).
    // Personalised prompts always lead. Action prompts (Upload) get pinned last.
    const merged = [
      ...dynamic.map(p => ({ ...p, _fixed: false })),
      ...fixed.map(p => ({ ...p, _fixed: true })),
    ];
    // Dedupe by lowercased text
    const seen = new Set();
    const deduped = [];
    for (const p of merged) {
      const key = (p.text || '').toLowerCase().trim();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      deduped.push(p);
    }
    // Cap at 4, but ensure at least one action prompt makes it (Upload) if available.
    const top = deduped.slice(0, 4);
    const hasAction = top.some(p => !!p.action);
    if (!hasAction) {
      const action = deduped.find(p => !!p.action);
      if (action) top[top.length - 1] = action;
    }
    return top.slice(0, 4);
  }, [fixed, dynamic]);

  const handleClick = (p) => {
    if (p.action) {
      navigate(p.action);
    } else {
      onTap(p.text);
    }
  };

  // Stage 20B+ Wave B — truncate the *displayed* prompt at 70 chars so
  // long personalised prompts don't wrap to 2 lines on mobile and lose
  // punch. Full text still flows through to the chat composer on tap
  // (via `p.text`) and is preserved for screen readers via `title`.
  const trunc = (s, n = 70) => {
    if (!s) return '';
    const t = String(s).trim();
    return t.length > n ? `${t.slice(0, n - 1).trimEnd()}…` : t;
  };

  return (
    <div className="space-y-2" data-testid="chat-starter-prompts">
      {all.map((p, i) => {
        const isAction = !!p.action;
        return (
          <motion.button
            key={p.id || i}
            onClick={() => handleClick(p)}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.04 * i }}
            className="w-full group flex items-center justify-between text-left rounded-xl bg-white border border-[#ECE8E1] hover:border-[#CFC8BB] hover:bg-[#FBFAF6] px-4 py-3 transition-colors"
            data-testid={isAction ? 'chat-starter-prompt-action' : (p._fixed ? 'chat-starter-prompt-fixed' : 'chat-starter-prompt-dynamic')}
            title={p.text}
          >
            <span className="flex items-center gap-2.5 pr-3 min-w-0 flex-1">
              {isAction && <UploadSimple size={14} className="text-[#2FB3A0] flex-shrink-0" weight="bold" />}
              <span className="text-[14px] text-[#0E1C2E] leading-snug truncate">{trunc(p.text)}</span>
            </span>
            <ArrowRight size={13} weight="bold" className="text-slate-400 group-hover:text-[#0E1C2E] transition-colors flex-shrink-0" />
          </motion.button>
        );
      })}

      {/* Dynamic skeleton while loading (only when we expect more coming) */}
      {!dynamicLoaded && (
        <>
          <div className="h-[46px] rounded-xl bg-[#F4F1EB] animate-pulse" />
          <div className="h-[46px] rounded-xl bg-[#F4F1EB] animate-pulse" />
        </>
      )}
    </div>
  );
};

export default ChatStarterPrompts;
